import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Sidebar.css";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const navLinks = [
    {
      path: "/dashboard",
      icon: "fa-tachometer",
      label: "Dashboard",
      show: true,
    },
    {
      path: "/dashboard/file-converter",
      icon: "fa-cloud-upload",
      label: "Convert Files",
      show: true,
    },
    {
      path: "/dashboard/files",
      icon: "fa-folder-open-o",
      label: "My Files",
      show: !!user,
    },
    {
      path: "/dashboard/usage",
      icon: "fa-line-chart",
      label: "Usage Analytics",
      show: !!user,
    },
    {
      path: "/dashboard/billing",
      icon: "fa-credit-card",
      label: "Billing & Plans",
      show: !!user,
    },
    {
      path: "/dashboard/admin/users",
      icon: "fa-users",
      label: "User Management",
      show: user?.role === "admin",
      adminOnly: true,
    },
  ];

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`dash-sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={toggleSidebar}
      ></div>

      {/* Sidebar */}
      <aside className={`dash-sidebar ${isOpen ? "open" : ""}`}>
        {/* Sidebar Header */}
        <div className="dash-sidebar-header">
          <div className="dash-sidebar-brand">
            <i className="fa fa-file-pdf-o dash-brand-icon"></i>
            <span className="dash-brand-text">
              tinypdfconverter.com
            </span>
          </div>
          <button className="dash-sidebar-close" onClick={toggleSidebar}>
            <i className="fa fa-times"></i>
          </button>
        </div>

        {/* User Info Card */}
        {user && (
          <div className="dash-user-card">
            <div className="dash-user-avatar">
              <i className="fa fa-user-circle"></i>
            </div>
            <div className="dash-user-info">
              <h6 className="dash-user-email">{user.email}</h6>
              <span
                className={`dash-user-badge ${
                  user.role === "admin" ? "admin" : "user"
                }`}
              >
                <i
                  className={`fa ${
                    user.role === "admin" ? "fa-shield" : "fa-user"
                  } me-1`}
                ></i>
                {user.role === "admin" ? "Administrator" : "User"}
              </span>
            </div>
          </div>
        )}

        {/* Credits Display */}
        {user && (
          <div className="dash-credits-card">
            <div className="dash-credits-icon">
              <i className="fa fa-battery-three-quarters"></i>
            </div>
            <div className="dash-credits-info">
              <span className="dash-credits-label">Available Credits</span>
              <span className="dash-credits-value">
                {user.subscription?.extractionCredits || 0}
              </span>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="dash-sidebar-nav">
          <ul className="dash-nav-list">
            {navLinks.map(
              (link) =>
                link.show && (
                  <li key={link.path} className="dash-nav-item">
                    <Link
                      to={link.path}
                      className={`dash-nav-link ${
                        isActive(link.path) ? "active" : ""
                      } ${link.adminOnly ? "admin-link" : ""}`}
                      onClick={(e) => {
                        // Only close sidebar on mobile (< 992px)
                        if (window.innerWidth < 992) {
                          toggleSidebar();
                        }
                      }}
                    >
                      <i className={`fa ${link.icon} dash-nav-icon`}></i>
                      <span className="dash-nav-text">{link.label}</span>
                      {isActive(link.path) && (
                        <span className="dash-nav-indicator"></span>
                      )}
                    </Link>
                  </li>
                )
            )}
          </ul>
        </nav>

        {/* Plan Upgrade CTA */}
        {user && user.subscription?.plan === "free" && (
          <div className="dash-sidebar-cta">
            <div className="dash-cta-content">
              <i className="fa fa-rocket dash-cta-icon"></i>
              <h6 className="dash-cta-title">Upgrade to Pro</h6>
              <p className="dash-cta-text">
                Get unlimited conversions and more features
              </p>
              <Link to="/dashboard/billing" className="dash-cta-button">
                Upgrade Now
              </Link>
            </div>
          </div>
        )}

        {/* Sidebar Footer */}
        <div className="dash-sidebar-footer">
          {/* Logout Button */}
          {user && (
            <button
              className="dash-logout-button"
              onClick={handleLogout}
              title="Logout"
            >
              <i className="fa fa-sign-out"></i>
              <span>Logout</span>
            </button>
          )}

          <p className="dash-footer-text">
            <i className="fa fa-copyright me-1"></i>
            {new Date().getFullYear()} tinypdfconverter.com
          </p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
